import React, { useState } from 'react';
import { Form, Button, Container } from 'react-bootstrap';
import { changePassword } from '@/api/myProfile';
import { getRequestId } from '@/api/LoginSignup';
import { encrypt } from '@/utils/crypto';
import { generateRandomID } from '@/utils/generateRandomID';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { setLoginModal, clearPassword } from '@/redux/login/loginSlice';
import { logOut, setPasswordExpired } from '@/redux/user/userSlice';
import { deleteCookie } from '@/utils/cookies';
import useAuth from '@/components/common/header/useAuth';


const ChangePassword = ({ isFromSignIn = false, onPasswordChangeSuccess, onClose }) => {
  const dispatch = useDispatch();
  const { userSignOut } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);



  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setError('');

    // Basic input sanitization
    const sqlInjectionPattern = /('|(\-\-)|;|\||\*|%|<|>|\+|=)/;
    if (sqlInjectionPattern.test(currentPassword) || sqlInjectionPattern.test(newPassword) || sqlInjectionPattern.test(confirmPassword)) {
      setError('Password must be between 8 and 20 characters long. Include a combination of uppercase and lowercase letters, numbers, and at least one of the following special characters: ! # @ % $ ; ^ May not be one of your previous 12 passwords.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('New password and confirm password do not match.');
      return;
    }

    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('Please fill in all fields.');
      return;
    }

    try {
      setLoading(true);
      const requestId = generateRandomID();
      await getRequestId({ requestId });

      const [encryptedCurrentPassword, encryptedNewPassword] = await Promise.all([
        encrypt(currentPassword),
        encrypt(newPassword)
      ]);

      const payload = {
        cryptoAlgVersion: "V3",
        newPassword: encryptedNewPassword,
        oldPassword: encryptedCurrentPassword,
        requestId
      };

      const response = await changePassword(payload);
      
      if (response?.SUCCESS) {
        toast.success('Password changed successfully!');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        await userSignOut({ isLoginModalRequired: false });
        dispatch(setPasswordExpired(false));
        dispatch(clearPassword());
        if (onPasswordChangeSuccess) {
          onPasswordChangeSuccess();
        } else {
          dispatch(setLoginModal(true));
        }
      } else  {
         setError(response?.ERROR || 'Failed to change password. Please try again.');
        } 
    } catch (error) {
      setError('An error occurred. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      {!isFromSignIn && <h2 className="my-4">Change Password</h2>}
      <form onKeyDown={(e) => e.key === 'Enter' && handleSubmit(e)}>
        <div className="mb-3">
          <label className="form-label" style={{ fontFamily: 'graphikregular', fontSize: '14px' }}>Current Password</label>
          <input
            id="change-password-current-input"
            type="password"
            className="form-control form-control-style"
            placeholder="Enter current password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            autoComplete="off"
            required
          />
        </div>

        <div className="mb-3">
          <label className="form-label" style={{ fontFamily: 'graphikregular', fontSize: '14px' }}>New Password</label>
          <input
            id="change-password-new-input"
            type="password"
            className="form-control form-control-style"
            placeholder="Enter new password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoComplete="new-password"
            required
          />
        </div>

        <div className="mb-3">
          <label className="form-label" style={{ fontFamily: 'graphikregular', fontSize: '14px' }}>Confirm New Password</label>
          <input
            id="change-password-confirm-input"
            type="password"
            className="form-control form-control-style"
            placeholder="Confirm new password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            autoComplete="new-password"
            required
          />
        </div>

        {error && <div className="error-text-color mb-3" dangerouslySetInnerHTML={{ __html: error }}></div>}

        <button
          id="change-password-submit-btn"
          type="button"
          className="btn btn-log w-100"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? 'Changing Password...' : 'Change Password'}
        </button>
      </form>
    </Container>
  );
};

export default ChangePassword;
